import { useCashflow } from '@/components/cashflow/CashflowContext';
import CategoryIcon from '@/components/cashflow/CategoryIcon';
import {
    AVERAGE_CHART_COLOR,
    AVERAGE_LABEL_COLOR,
    getArrowVisibility,
} from '@/components/cashflow/charts/chartHelpers';
import TransactionsLineChart, {
    SIMPLE_SPENDING_COLOR,
} from '@/components/cashflow/charts/TransactionsLineChart';
import { LabelShape } from '@/components/common/charts/ChartTooltip';
import PieChart from '@/components/common/charts/PieChart';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import FadeInBox from '@/components/common/FadeInBox';
import { FlexRow } from '@/components/common/Flex';
import HeaderLink from '@/components/common/HeaderLink';
import { Expand04 } from '@/components/common/icons';
import { Skeletons } from '@/components/common/Skeletons';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { useTracking } from '@/services/analytics/useTracking';
import { buildSpendingGroups } from '@/services/cashflow';
import { toDollars } from '@/util/currency';
import { Divider, Skeleton, Text } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    CashflowTransactionEntry,
    getTransactionChartData,
} from '@common/react/cashflow';
import { useHasNoFEs } from '@common/react/financialEntities';
import { sum } from '@common/util/arrays';
import { dt, getRangeUnit } from '@common/util/dateTime';
import { ParentSize } from '@visx/responsive';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { ChartRangeStepperIconButton } from '../common/charts/ChartRangeControls';
import { FlexCol } from '../common/Flex';

const CATEGORY_COLORS = ['#618DDA', '#3F5D7F', '#D07E44', '#F0CC79', '#EDF0F3'];

const DashboardSpendingWidget = () => {
    const { track } = useTracking();
    const router = useRouter();
    const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
    const hasNoFEs = useHasNoFEs();

    const {
        transactionEntries,
        historicalTransactionEntries,
        loading,
        dateRange,
        setDateRange,
        setNextDateRange,
        isAllTimeDateRange,
    } = useCashflow();

    const dateRangeUnit = getRangeUnit(dateRange);
    const spendingGroups = useSpendingGroups(transactionEntries);
    const total = sum(spendingGroups, (c) => c.amount);

    const { valueSeries, range } = useMemo(
        () =>
            getTransactionChartData({
                transactionEntries,
                historicalTransactionEntries,
                dateRange,
            }),
        [transactionEntries, historicalTransactionEntries, dateRange]
    );

    // Show sample data for the previous month so we can have a full month's worth of data
    useEffect(() => {
        if (hasNoFEs) {
            setDateRange({
                startDate: dt()
                    .subtract(1, 'month')
                    .startOf('month')
                    .toDateStr(),
                endDate: dt().subtract(1, 'month').endOf('month').toDateStr(),
            });
        } else {
            // When FEs are added, show the current month
            setDateRange({
                startDate: dt().startOf('month').toDateStr(),
                endDate: dt().endOf('month').toDateStr(),
            });
        }
        // Including setDateRange in dependencies causes navigation issues
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNoFEs]);

    const { showNext, showPrevious } = getArrowVisibility(
        dateRange,
        isAllTimeDateRange
    );

    const monthStr = dt(dateRange.startDate).format('MMMM');
    const isCurrentMonth = dt(dateRange.startDate).isSame(dt(), 'month');

    const pieData = loading
        ? Array.from({ length: 5 }).map((_, i) => ({
              key: i.toString(),
              value: i / 5,
              color: CATEGORY_COLORS[i],
              label: i.toString(),
          }))
        : spendingGroups.map((group, i) => ({
              key: i.toString(),
              value: group.amount / total,
              color: CATEGORY_COLORS[i],
              label: group.name,
          }));

    const viewSpendingGroup = (
        group: ReturnType<typeof useSpendingGroups>[number]
    ) => {
        router.push({
            pathname: '/cashflow/transactions',
            query: {
                ...('category' in group && {
                    categoryIds: group.category.id,
                }),
                ...('categoryIds' in group && {
                    categoryIds: group.categoryIds.join(','),
                }),
                ...(!isCurrentMonth && dateRange),
            },
        });
    };

    return (
        <FlexCol mt="29px">
            {loading ? (
                <Skeleton w={210} h={5} />
            ) : (
                <FlexRow justify="space-between" align="center">
                    <HeaderLink
                        onClick={() => {
                            router.push({
                                pathname: '/cashflow',
                                query: {
                                    ...(!isCurrentMonth && dateRange),
                                },
                            });
                        }}
                        title={`${monthStr} spending ${isCurrentMonth ? 'is' : 'was'} ${toDollars(-total, { withCents: false })}`}
                    />
                    <FlexRow
                        basis={24}
                        grow={0}
                        align="center"
                        gap={2}
                        justify="flex-end"
                    >
                        <ChartRangeStepperIconButton
                            type="decrement"
                            onClick={() => setNextDateRange(false)}
                            onClickEvent="Dashboard Spending Chart Range Decrement Button Clicked"
                            onClickEventParams={{ dateRangeUnit }}
                            tooltip={
                                dateRangeUnit === 'custom'
                                    ? 'Previous'
                                    : `Previous ${dateRangeUnit}`
                            }
                            size={6}
                            color="textMuted"
                            isDisabled={!showPrevious}
                        />
                        <ChartRangeStepperIconButton
                            type="increment"
                            onClick={() => setNextDateRange(true)}
                            onClickEvent="Dashboard Spending Chart Range Increment Button Clicked"
                            onClickEventParams={{ dateRangeUnit }}
                            tooltip={
                                dateRangeUnit === 'custom'
                                    ? 'Next'
                                    : `Next ${dateRangeUnit}`
                            }
                            size={6}
                            color="textMuted"
                            isDisabled={!showNext}
                        />
                    </FlexRow>
                </FlexRow>
            )}
            <Divider mt={4} mb={4} />
            <FlexRow gap={4} ml={2}>
                <FlexRow
                    fontSize="sm"
                    align="center"
                    color={SIMPLE_SPENDING_COLOR}
                >
                    <LabelShape color={SIMPLE_SPENDING_COLOR} shape="circle" />
                    Actual
                </FlexRow>
                <FlexRow
                    fontSize="sm"
                    align="center"
                    color={AVERAGE_LABEL_COLOR}
                >
                    <LabelShape color={AVERAGE_CHART_COLOR} shape="circle" />3
                    month avg
                </FlexRow>
            </FlexRow>

            <FadeInBox>
                <FlexRow grow={1} position="relative" h={40}>
                    <ParentSize>
                        {(parent) => (
                            <ErrorBoundary>
                                <TransactionsLineChart
                                    width={parent.width}
                                    height={parent.height}
                                    valueSeries={valueSeries}
                                    range={range}
                                    selectedSupercategories={['SPENDING']}
                                    variant="simple"
                                />
                            </ErrorBoundary>
                        )}
                    </ParentSize>
                </FlexRow>
            </FadeInBox>
            <Divider mb={8} mt={-5} zIndex={-1} />
            <FlexRow gap={4} pr={4} align="center">
                {(loading || spendingGroups.length > 0) && (
                    <PieChart
                        data={pieData}
                        h={40}
                        w={40}
                        flex={1}
                        onMouseEnter={(datum) => {
                            assert(datum.label);
                            setHoveredCategory(datum.label);
                        }}
                        onMouseLeave={() => {
                            setHoveredCategory(null);
                        }}
                        onClick={(datum) => {
                            const spendingGroup = spendingGroups.find(
                                (group) => group.name === datum.label
                            );

                            if (spendingGroup) {
                                track(
                                    'Dashboard Spending Pie Chart Slice Clicked',
                                    { name: spendingGroup.name }
                                );
                                viewSpendingGroup(spendingGroup);
                            }
                        }}
                    />
                )}
                <FlexCol flex={1} gap={1}>
                    {loading && <Skeletons count={5} h={6} my={1} />}
                    {spendingGroups.map((group) => (
                        <FlexRow
                            justify="space-between"
                            align="center"
                            key={group.name}
                            fontSize="sm"
                            {...hoverStyleProps({
                                bg: 'gray.100',
                            })}
                            bg={
                                hoveredCategory === group.name
                                    ? 'gray.100'
                                    : undefined
                            }
                            p={2}
                            mx={-2}
                            borderRadius="md"
                            onClick={() => {
                                track('Dashboard Spending Group Clicked', {
                                    name: group.name,
                                });
                                viewSpendingGroup(group);
                            }}
                        >
                            <FlexRow align="center" gap={2}>
                                {group.icon}
                                <Text color="gray.500">{group.name}</Text>
                            </FlexRow>
                            <Text color="black" fontWeight="medium">
                                {toDollars(group.amount, {
                                    withCents: true,
                                })}
                            </Text>
                        </FlexRow>
                    ))}
                </FlexCol>
            </FlexRow>
            <Divider mb={10} mt={8} />
        </FlexCol>
    );
};

const useSpendingGroups = (transactionEntries: CashflowTransactionEntry[]) => {
    return useMemo(() => {
        const spendingGroups = buildSpendingGroups({
            transactionEntries: transactionEntries,
            groupBy: 'category',
        }).map((group, index) => ({
            ...group,
            icon: (
                <CategoryIcon
                    category={group.category}
                    color={CATEGORY_COLORS[index]}
                    withBackground={false}
                />
            ),
        }));

        if (spendingGroups.length === 0) return [];

        const otherGroup = {
            id: 'other',
            name: 'Everything else',
            amount: 0,
            categoryIds: [] as string[],
            icon: <Expand04 color="gray.400" size="sm" />,
        };

        spendingGroups.slice(4, spendingGroups.length).forEach((group) => {
            otherGroup.categoryIds.push(group.category.id);
            otherGroup.amount += group.amount;
        });

        return [...spendingGroups.slice(0, 4), otherGroup];
    }, [transactionEntries]);
};

export default DashboardSpendingWidget;
